/**
 * emojiToSemanticToken — Phase 4 Part 2.
 *
 * Purpose
 * -------
 * The backend `/api/dashboard/summary` response includes emoji glyph
 * fields for domain categories (e.g. `mortgageTier.emoji = "🏠"`,
 * `category.emoji = "🎯"`) as part of a stable API contract that the
 * frontend cannot change unilaterally.
 *
 * The executive redesign bans emoji glyphs in the final DOM (see
 * `phase_3_client_experience.test.jsx` and `check_page_migration.sh`),
 * so the frontend must translate the incoming glyph to a semantic
 * token before rendering. This module owns that mapping.
 *
 * Contract
 * --------
 *   emojiToSemanticToken(rawGlyph: string | null | undefined) → {
 *     token: "goal" | "achievement" | "milestone" | "warning" |
 *            "info" | "shield" | "neutral",
 *     tone:  "accent" | "success" | "warning" | "info" | "neutral",
 *     Icon:  React component (lucide-react),
 *     ariaLabel: string,    // announced to screen readers instead of the glyph
 *   }
 *
 * Unknown / unmapped input falls back to `"neutral"`. Callers should
 * NEVER render the raw glyph — that would defeat the whole point.
 */

import { Target, Award, Trophy, TriangleAlert, Info, Home, ShieldCheck, CircleDot } from "lucide-react";

const NEUTRAL = {
  token: "neutral",
  tone: "neutral",
  Icon: CircleDot,
  ariaLabel: "Status",
};

// Backend-emitted glyphs → semantic tokens. Ordered by expected frequency.
// Any glyph not listed here falls back to `NEUTRAL`.
const MAP = {
  // Category
  "🎯": { token: "goal",        tone: "accent",  Icon: Target,        ariaLabel: "Goal" },
  "🏆": { token: "achievement", tone: "success", Icon: Trophy,        ariaLabel: "Achievement" },
  "🎉": { token: "milestone",   tone: "accent",  Icon: Award,         ariaLabel: "Milestone" },
  "🚀": { token: "milestone",   tone: "accent",  Icon: Award,         ariaLabel: "Launch" },
  // Mortgage tier
  "🏠": { token: "milestone",   tone: "info",    Icon: Home,          ariaLabel: "Home" },
  "🏡": { token: "milestone",   tone: "info",    Icon: Home,          ariaLabel: "Home ready" },
  // Warnings / info
  "⚠️": { token: "warning",     tone: "warning", Icon: TriangleAlert, ariaLabel: "Warning" },
  "ℹ️": { token: "info",        tone: "info",    Icon: Info,          ariaLabel: "Information" },
  // Shield / verified
  "🛡️": { token: "shield",      tone: "info",    Icon: ShieldCheck,   ariaLabel: "Verified" },
  "✅": { token: "achievement", tone: "success", Icon: ShieldCheck,   ariaLabel: "Confirmed" },
  "✨": { token: "milestone",   tone: "accent",  Icon: Award,         ariaLabel: "Highlight" },
};

/**
 * Return the semantic descriptor for a given backend emoji glyph. Any
 * unknown / null / non-string input resolves to `NEUTRAL` (never to
 * the raw glyph).
 */
export function emojiToSemanticToken(glyph) {
  if (typeof glyph !== "string" || !glyph) return NEUTRAL;
  return MAP[glyph] || NEUTRAL;
}

/**
 * Exposed for tests + admin analytics. Do NOT export the raw MAP by
 * reference to callers — enumeration is fine but mutation must never
 * happen from a caller.
 */
export function knownEmojiGlyphs() {
  return Object.keys(MAP);
}

export default emojiToSemanticToken;
